import { getUuid } from "@util/getUuid";

export const SNAPSHOT_QUERY_VISUALIZER_DATA_VERSION = 1;

function formatQueryDataRequest({
  queryComponents = [],
  queryData = {},
  locations = [],
  organizations = [],
}) {
  const selectFieldData = queryComponents
    .filter((m) => m.type === "select")
    .reduce((acc, cur) => {
      if (Array.isArray(queryData[cur.server_name])) {
        acc[cur.server_name] = queryData[cur.server_name].map((m) => {
          if (typeof m === "object") {
            return m.value;
          } else {
            return m;
          }
        });
      } else {
        acc[cur.server_name] = queryData[cur.clientName];
      }
      return acc;
    }, {});

  return {
    ...selectFieldData,
    subject_locations: locations.map((m) => m.id),
    subject_organizations: organizations.map((m) => m.id),
  };
}

function formatQueryComponentViz({
  queryComponents = [],
  queryData = {},
  locations = [],
  organizations = [],
}) {
  return [
    {
      type: "subject_locations",
      server_name: "subject_locations",
      ids: locations.map((m) => m.id),
      label: "Locations",
      display_keys: [],
    },
    {
      type: "subject_organizations",
      server_name: "subject_organizations",
      ids: organizations.map((m) => m.id),
      label: "Organizations",
      display_keys: [],
    },
    ...queryComponents.map((qc) => {
      const formattedQc = {
        id: getUuid(),
        label: qc.label,
        tooltip_text: qc.tooltip_text || "",
        server_name: qc.server_name,
        type: qc.type === "select" ? "select" : "pass_through",
      };

      if (Array.isArray(queryData[qc.server_name])) {
        formattedQc.ids = queryData[qc.server_name].map((m) => {
          if (typeof m === "object") {
            return m.value;
          } else {
            return m;
          }
        });
      } else {
        formattedQc.value = qc.options
          ? qc.options.find((m) => m.value === queryData[qc.server_name])
          : queryData[qc.server_name];
      }

      return formattedQc;
    }),
  ].map((m) => ({
    ...m,
    id: getUuid(),
    display_keys: getDisplayKeys(m.server_name),
    query_data_request: m.server_name,
  }));
}

export function retrofitQueryVizData(data) {
  const { recipe, query_data: queryData, snapshot } = data;
  if (!recipe || !queryData || !snapshot) return null;
  const queryDataRequest = formatQueryDataRequest({
    queryComponents: recipe.query_components,
    locations: queryData?.saved_recipe_locations,
    organizations: queryData?.saved_recipe_orgs,
    queryData,
  });
  const queryComponentViz = formatQueryComponentViz({
    queryComponents: recipe.query_components,
    locations: queryData?.saved_recipe_locations,
    organizations: queryData?.saved_recipe_orgs,
    queryData,
  });
  return {
    snapshot_data: {
      query_data_request: queryDataRequest,
      query_component_viz: {
        version: SNAPSHOT_QUERY_VISUALIZER_DATA_VERSION,
        display_components: queryComponentViz,
        data: {},
      },
    },
  };
}

export default function qbSnapshotQueryVisualizerData(
  queryComponents,
  queryData,
  recipeInfo = null,
  entityData = {
    locations: [],
    organizations: [],
  }
) {
  // if (!queryComponents) return true;
  const req = {
    query_options: 1,
    visualizer: queryData.visualizer,
  };

  if (recipeInfo?.custom_report_comp === "response_filter") {
    const questionsToSave = queryData.selected_questions.filter(
      (f) => f.selected_items.length > 0
    );
    const uniqueQuestionCount = questionsToSave.length;
    const formattedQuestions = queryData.selected_questions.map((m) => ({
      question_id: m.id,
      select_item_ids: m.selected_items.map((m) => m.id),
    }));
    req.snapshot_data = {
      query_data_request: {
        subject_locations: entityData.locations.map((m) => m.id),
        subject_organizations: entityData.organizations.map((m) => m.id),
        select_item_ids: formattedQuestions.flatMap((m) => m.select_item_ids),
        campaign_efforts: [queryData.campaign_effort_id],
      },
      query_component_viz: {
        version: SNAPSHOT_QUERY_VISUALIZER_DATA_VERSION,
        // select_item_ids: formattedQuestions.flatMap((m) => m.select_item_ids),
        // or_query: uniqueQuestionCount === 1,
        // saved_selected_questions: questionsToSave,
        display_components: [
          {
            type: "subject_locations",
            server_name: "subject_locations",
            ids: entityData.locations.map((m) => m.id),
            label: "Locations",
            display_keys: [],
          },
          {
            type: "subject_organizations",
            server_name: "subject_organizations",
            ids: entityData.organizations.map((m) => m.id),
            label: "Organizations",
            display_keys: [],
          },
          {
            type: "select",
            server_name: "campaign_efforts",
            ids: [queryData.campaign_effort_id],
            label: "Campaign Efforts",
            display_keys: [],
          },
          {
            id: getUuid(),
            label: "Questions",
            tooltip_text: "",
            server_name: "questions",
            type: "pass_through",
            value: questionsToSave,
          },
        ].map((m) => ({
          ...m,
          id: getUuid(),
          display_keys: getDisplayKeys(m.server_name),
          query_data_request: m.server_name,
        })),
      },
    };
  } else {
    const queryDataSelectFieldIds = queryComponents
      .filter((m) => m.type === "select")
      .reduce((acc, cur) => {
        if (Array.isArray(queryData[cur.clientName])) {
          acc[cur.server_name] = queryData[cur.clientName].map((m) => {
            if (typeof m === "object") {
              return m.value;
            } else {
              return m;
            }
          });
        } else {
          acc[cur.server_name] = queryData[cur.clientName];
        }
        return acc;
      }, {});

    req.snapshot_data = {
      query_data_request: {
        ...queryDataSelectFieldIds,
        subject_locations: entityData.locations.map((m) => m.id),
        subject_organizations: entityData.organizations.map((m) => m.id),
      },
      query_component_viz: {
        version: SNAPSHOT_QUERY_VISUALIZER_DATA_VERSION,
        display_components: [
          {
            type: "subject_locations",
            server_name: "subject_locations",
            ids: entityData.locations.map((m) => m.id),
            label: "Locations",
            display_keys: [],
          },
          {
            type: "subject_organizations",
            server_name: "subject_organizations",
            ids: entityData.organizations.map((m) => m.id),
            label: "Organizations",
            display_keys: [],
          },
          ...queryComponents.map((qc) => {
            const formattedQc = {
              id: getUuid(),
              label: qc.label,
              tooltip_text: qc.tooltip_text || "",
              server_name: qc.server_name,
              type: qc.type === "select" ? "select" : "pass_through",
            };

            if (Array.isArray(queryData[qc.clientName])) {
              formattedQc.ids = queryData[qc.clientName].map((m) => {
                if (typeof m === "object") {
                  return m.value;
                } else {
                  return m;
                }
              });
            } else {
              formattedQc.value = qc.options
                ? qc.options.find((m) => m.value === queryData[qc.clientName])
                : queryData[qc.clientName];
            }

            return formattedQc;
          }),
        ].map((m) => ({
          ...m,
          id: getUuid(),
          display_keys: getDisplayKeys(m.server_name),
          query_data_request: m.server_name,
        })),
        data: {},
      },
    };
  }

  return req.snapshot_data || {};
}

function getDisplayKeys(serverName) {
  switch (serverName) {
    case "contests":
      return ["name", "start_date", "end_date"];
    case "campaign_efforts":
      return ["title", "start_date", "end_date", "internal_name"];
    case "keywords":
      return ["text"];
    case "locations":
      return ["name", "city", "state_province"];
    case "subject_locations":
      return ["name", "city", "state_province"];
    case "subject_organizations":
      return ["name", "city", "state_province"];
    default:
      return [];
  }
}
